import { memo, useCallback, useState } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router } from "expo-router";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";
import { AssignSiteRolesList, type SiteRole, type SiteWithCameras } from "./AssignSiteRolesList";

export type { SiteWithCameras };

type Role = SiteRole;

interface AssignSiteRolesBottomSheetOuterProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  userId: string;
  userName: string;
  sites: SiteWithCameras[];
  onSave: (assignments: { siteId: string; role: Role }[]) => Promise<void> | void;
}

interface AssignSiteRolesBottomSheetInnerProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  userId: string;
  userName: string;
  savedSites: SiteWithCameras[];
  onSave: (assignments: { siteId: string; role: Role }[]) => Promise<void> | void;
}

const AssignSiteRolesBottomSheetInner = memo(
  ({ bottomSheetRef, userId, userName, savedSites, onSave }: AssignSiteRolesBottomSheetInnerProps) => {
    const { theme } = useUnistyles();
    const { bottom } = useSafeAreaInsets();
    const [roleMap, setRoleMap] = useState<Map<string, Role>>(
      () => new Map(savedSites.map((site) => [site.siteId, "viewer"])),
    );

    const handleRoleChange = useCallback((siteId: string, role: Role) => {
      setRoleMap((prev) => {
        const next = new Map(prev);
        next.set(siteId, role);
        return next;
      });
    }, []);

    const handleSave = useCallback(async () => {
      const assignments = savedSites.map((site) => ({
        siteId: site.siteId,
        role: roleMap.get(site.siteId) ?? ("viewer" as Role),
      }));

      try {
        await onSave(assignments);
      } catch {
        return;
      }

      bottomSheetRef.current?.dismiss();
      setTimeout(() => {
        router.dismissTo(`/settings/site-access/camera-access/${encodeURIComponent(userId)}`);
      }, 300);
    }, [savedSites, roleMap, onSave, bottomSheetRef, userId]);

    return (
      <AppBottomSheet
        bottomSheetRef={bottomSheetRef}
        snapPoints={["60%"]}
        enableDynamicSizing={false}
        backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
      >
        <View flex={1} style={[styles.container, { paddingBottom: bottom + theme.spacing["3xl"] }]}>
          <Text textStyle="md/medium" style={styles.title}>
            Choose {userName}&apos;s roles
          </Text>

          <BottomSheetScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
            <AssignSiteRolesList sites={savedSites} roleMap={roleMap} onRoleChange={handleRoleChange} />
          </BottomSheetScrollView>

          <View pt="3xl">
            <Button variant="primary" label="Save" size="md" onPress={handleSave} />
          </View>
        </View>
      </AppBottomSheet>
    );
  },
);

interface AssignSiteRolesBottomSheetProps extends AssignSiteRolesBottomSheetOuterProps {}

export const AssignSiteRolesBottomSheet = memo(
  ({ bottomSheetRef, userId, userName, sites, onSave }: AssignSiteRolesBottomSheetProps) => {
    if (sites.length === 0) return null;

    const key = sites
      .map((site) => site.siteId)
      .sort()
      .join("-");

    return (
      <AssignSiteRolesBottomSheetInner
        key={key}
        bottomSheetRef={bottomSheetRef}
        userId={userId}
        userName={userName}
        savedSites={sites}
        onSave={onSave}
      />
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
  title: {
    marginVertical: theme.spacing["4xl"],
  },
  scrollContent: {
    flexGrow: 1,
  },
}));
